import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BsDropdownDirective } from 'ngx-bootstrap/dropdown';

export type ProfileOption = {
  id: string;
  name: string;
  isSystem?: boolean;
};

@Component({
  selector: 'app-profile-multi-select',
  templateUrl: './profile-multi-select.component.html',
  styleUrls: ['./profile-multi-select.component.css'],
  encapsulation: ViewEncapsulation.None, // <- IMPORTANTE p/ estilizar menu no body
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ProfileMultiSelectComponent),
      multi: true,
    },
  ],
})
export class ProfileMultiSelectComponent
  implements ControlValueAccessor, OnChanges, OnInit, AfterViewInit
{
  @Input() profiles: ProfileOption[] = [];
  @Input() placeholder = 'Selecione perfis';
  @Input() disabled = false;
  /** Ids de perfis travados: não podem ser marcados/desmarcados nem removidos
      (ex.: o perfil Proprietário para quem não é Proprietário). */
  @Input() lockedIds: string[] = [];

  value: string[] = [];
  filtered: ProfileOption[] = [];
  search = '';

  dropdownWidth = 0;
  countPlus = 0;

  @ViewChild('multiDropdownContainer', { static: false, read: ElementRef })
  multiDropdownContainer?: ElementRef<HTMLDivElement>;

  @ViewChild('multiDropdown', { static: false })
  multiDropdown?: BsDropdownDirective;

  @ViewChild('tagsSelected', { static: false, read: ElementRef })
  tagsSelected?: ElementRef<HTMLDivElement>;

  private onChange: (val: string[]) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit(): void {
    this.applyFilter();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['profiles']) {
      this.applyFilter();
      setTimeout(() => this.calcLenSelect(), 0);
    }
  }

  ngAfterViewInit(): void {
    this.updateDropdownWidth();
    this.calcLenSelect();

    if (this.multiDropdown) {
      this.multiDropdown.onShown.subscribe(() => {
        this.updateDropdownWidth();
        this.applyFilter();
        this.calcLenSelect();
      });
    }
  }

  // ----- CVA -----
  writeValue(val: string[] | null): void {
    this.value = Array.isArray(val) ? [...val] : [];
    setTimeout(() => this.calcLenSelect(), 0);
  }

  registerOnChange(fn: (val: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  applyFilter(): void {
    const q = (this.search || '').trim().toLowerCase();
    const list = this.profiles || [];
    this.filtered = !q ? list : list.filter((p) => p.name.toLowerCase().includes(q));
  }

  isChecked(id: string): boolean {
    return this.value.includes(id);
  }

  isLocked(id: string): boolean {
    return this.lockedIds.includes(id);
  }

  toggle(id: string): void {
    if (this.disabled) return;
    if (this.isLocked(id)) return;

    const next = this.isChecked(id)
      ? this.value.filter((x) => x !== id)
      : [...this.value, id];

    this.value = next;
    this.onChange([...next]);
    this.onTouched();

    setTimeout(() => this.calcLenSelect(), 0);
  }

  remove(id: string): void {
    if (this.disabled) return;
    if (this.isLocked(id)) return;
    if (!this.isChecked(id)) return;

    const next = this.value.filter((x) => x !== id);
    this.value = next;
    this.onChange([...next]);
    this.onTouched();

    setTimeout(() => this.calcLenSelect(), 0);
  }

  clear(): void {
    if (this.disabled) return;
    this.value = [];
    this.onChange([]);
    this.onTouched();
    setTimeout(() => this.calcLenSelect(), 0);
  }

  nameById(id: string): string {
    return (this.profiles || []).find((p) => p.id === id)?.name ?? id;
  }

  private updateDropdownWidth(): void {
    const el = this.multiDropdownContainer?.nativeElement;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    this.dropdownWidth = rect.width;
  }

  // ----- +N -----
  private calcLenSelect(): void {
    const container = this.multiDropdownContainer?.nativeElement;
    const tagsEl = this.tagsSelected?.nativeElement;

    if (!container || !tagsEl) {
      this.countPlus = 0;
      return;
    }

    let sum = 0;
    let countPlus = 0;
    const maxWidth = container.offsetWidth * 0.9;

    Array.from(tagsEl.children).forEach((child) => {
      const el = child as HTMLElement;

      if (el.nodeName !== 'DIV') return;

      el.hidden = false;

      const w = el.offsetWidth + 16;
      if (sum + w < maxWidth && countPlus === 0) {
        sum += w;
      } else {
        countPlus += 1;
        el.hidden = true;
      }
    });

    this.countPlus = countPlus;
  }

  @HostListener('document:keydown.escape')
  onEscapePress(): void {
    if (this.multiDropdown?.isOpen) this.multiDropdown.hide();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateDropdownWidth();
    this.calcLenSelect();
  }
}